import React, { createContext, useContext, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { backend_api } from "../../static/server";
import { upsertTaskFromLexmomAi } from "../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";

const ChatContext = createContext();

const DEFAULT_BOT_REPLY = "I processed your request.";

const ROUTE_MATCHERS = [
  {
    keywords: ["matter", "matters", "case", "cases"],
    label: "Open matters",
    route: "/dashboard/matter",
  },
  {
    keywords: ["task", "tasks"],
    label: "Open tasks",
    route: "/dashboard/tasks",
  },
  {
    keywords: ["document", "documents", "folder", "folders", "template"],
    label: "Open documents",
    route: "/dashboard/documents",
  },
  {
    keywords: ["activity", "activities"],
    label: "Open activities",
    route: "/dashboard/activities",
  },
  {
    keywords: ["bill", "bills", "invoice", "invoices"],
    label: "Open bills",
    route: "/dashboard/bills",
  },
  {
    keywords: ["ai workspace", "ai manager", "assistant", "assistants"],
    label: "Open AI workspace",
    route: "/dashboard/ai-workspace",
  },
];

const RESPONSE_PROMPT_SUGGESTIONS = {
  matter_count: [
    {
      id: "matter-count-pending",
      label: "Pending matters",
      prompt: "How many pending matters do I have right now?",
    },
    {
      id: "matter-count-summary",
      label: "Matter summary",
      prompt: "Summarize my current matter status breakdown.",
    },
    {
      id: "matter-count-closed",
      label: "Closed matters",
      prompt: "How many closed matters do I have?",
    },
  ],
  task_count: [
    {
      id: "task-count-pending",
      label: "Pending tasks",
      prompt: "How many pending tasks do I have right now?",
    },
    {
      id: "task-count-completed",
      label: "Completed tasks",
      prompt: "How many completed tasks do I have?",
    },
    {
      id: "task-count-summary",
      label: "Task summary",
      prompt: "Summarize my current task status breakdown.",
    },
  ],
  matter_insight: [
    {
      id: "matter-insight-open",
      label: "Open matters",
      prompt: "Show me open matter insights.",
    },
    {
      id: "matter-insight-filters",
      label: "Matter filters",
      prompt: "Explain the matter filters used for this answer.",
    },
    {
      id: "matter-insight-next",
      label: "Next actions",
      prompt: "What should I review next in my matters?",
    },
  ],
  task_insight: [
    {
      id: "task-insight-open",
      label: "Open tasks",
      prompt: "Show me open task insights.",
    },
    {
      id: "task-insight-filters",
      label: "Task filters",
      prompt: "Explain the task filters used for this answer.",
    },
    {
      id: "task-insight-next",
      label: "Next actions",
      prompt: "What tasks need my attention next?",
    },
  ],
  general_insight: [
    {
      id: "general-insight-summary",
      label: "Summarize this",
      prompt: "Summarize this response in one sentence.",
    },
    {
      id: "general-insight-filters",
      label: "Explain filters",
      prompt: "Explain the filters and scope used for this answer.",
    },
    {
      id: "general-insight-followup",
      label: "What next",
      prompt: "What is the best follow-up question here?",
    },
  ],
};

export const DEFAULT_PROMPT_SUGGESTIONS = [
  {
    id: "default-open-matters",
    label: "Open matters",
    prompt: "How many open matters do I have right now?",
  },
  {
    id: "default-open-tasks",
    label: "Open tasks",
    prompt: "How many open tasks do I have right now?",
  },
  {
    id: "default-matter-breakdown",
    label: "Matter breakdown",
    prompt: "Give me a quick breakdown of my matter statuses.",
  },
  {
    id: "default-task-breakdown",
    label: "Task breakdown",
    prompt: "Give me a quick breakdown of my task statuses.",
  },
];

export const DEFAULT_CHAT_NAVIGATION = [
  { id: "nav-matters", label: "Matters", route: "/dashboard/matter" },
  { id: "nav-tasks", label: "Tasks", route: "/dashboard/tasks" },
  { id: "nav-documents", label: "Documents", route: "/dashboard/documents" },
  {
    id: "nav-ai-rules",
    label: "AI Rules",
    route: "/dashboard/settings/ai-rules",
  },
  {
    id: "nav-ai-workspace",
    label: "AI Workspace",
    route: "/dashboard/ai-workspace",
  },
];

const createMessageId = (prefix = "msg") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const toCleanString = (value) =>
  typeof value === "string" ? value.trim() : "";

const splitCamelCase = (value) =>
  value.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

const normalizeHumanText = (value) =>
  splitCamelCase(value).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

const humanizeInlineText = (value) => {
  const cleaned = toCleanString(value);
  return cleaned ? normalizeHumanText(cleaned) : "";
};

const humanizeLabel = (value) => {
  const cleaned = humanizeInlineText(value).toLowerCase();

  if (!cleaned) {
    return "";
  }

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const uniqueBy = (items, getKey) => {
  const seen = new Set();

  return items.filter((item) => {
    const key = getKey(item);

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const createChatMessage = (sender, text, extra = {}) => ({
  id: createMessageId(sender === "user" ? "user" : "bot"),
  text,
  sender,
  timestamp: new Date().toISOString(),
  ...extra,
});

const normalizeMetric = (metric, index) => {
  if (!metric || typeof metric !== "object") {
    return null;
  }

  const numericValue = Number(metric.value);

  return {
    id: toCleanString(metric.id) || `metric-${index + 1}`,
    label:
      humanizeLabel(metric.label || metric.id) || `Metric ${index + 1}`,
    value: Number.isFinite(numericValue) ? numericValue : metric.value,
  };
};

const normalizeTable = (table, index) => {
  if (!table) {
    return null;
  }

  if (typeof table === "string") {
    return {
      table: humanizeLabel(table),
      rowCount: null,
      scopedBy: null,
      appliedFilters: [],
    };
  }

  if (typeof table !== "object") {
    return null;
  }

  const rawRowCount = table.row_count ?? table.rowCount ?? table.count ?? null;
  const parsedRowCount = Number(rawRowCount);

  return {
    table:
      humanizeLabel(table.table || table.name || table.id) ||
      `Table ${index + 1}`,
    rowCount: Number.isFinite(parsedRowCount) ? parsedRowCount : null,
    scopedBy: humanizeLabel(table.scoped_by || table.scopedBy) || null,
    appliedFilters: toArray(table.applied_filters || table.appliedFilters)
      .map(humanizeInlineText)
      .filter(Boolean),
  };
};

const normalizePromptSuggestion = (suggestion, index) => {
  if (!suggestion || typeof suggestion !== "object") {
    return null;
  }

  const prompt = toCleanString(suggestion.prompt || suggestion.value);
  const label = toCleanString(suggestion.label) || prompt;

  if (!prompt || !label) {
    return null;
  }

  return {
    id: toCleanString(suggestion.id) || `suggestion-${index + 1}`,
    label,
    prompt,
  };
};

const normalizePromptSuggestions = (suggestions) =>
  uniqueBy(
    toArray(suggestions).map(normalizePromptSuggestion).filter(Boolean),
    (suggestion) => suggestion.prompt.toLowerCase(),
  );

const NORMALIZED_DEFAULT_PROMPT_SUGGESTIONS = normalizePromptSuggestions(
  DEFAULT_PROMPT_SUGGESTIONS,
);

const normalizeStructuredResponse = (structuredResponse, prompt) => {
  if (!structuredResponse || typeof structuredResponse !== "object") {
    return {
      version: "1.0",
      responseType: null,
      prompt,
      summary: "",
      uiActions: [],
      metrics: [],
      tables: [],
    };
  }

  return {
    version: toCleanString(structuredResponse.version) || "1.0",
    responseType: toCleanString(structuredResponse.response_type) || null,
    prompt: toCleanString(structuredResponse.prompt) || prompt,
    summary: humanizeInlineText(structuredResponse.summary),
    uiActions: toArray(structuredResponse.ui_actions),
    metrics: toArray(structuredResponse.metrics)
      .map(normalizeMetric)
      .filter(Boolean),
    tables: toArray(structuredResponse.tables)
      .map(normalizeTable)
      .filter(Boolean),
  };
};

const normalizeContextTables = (contextTables) =>
  toArray(contextTables).map(normalizeTable).filter(Boolean);

const normalizeUiAction = (action, index) => {
  if (!action || typeof action !== "object") {
    return null;
  }

  const type = toCleanString(action.type);

  if (!type) {
    return null;
  }

  return {
    id: toCleanString(action.id) || `ui-action-${index + 1}`,
    type,
    route: toCleanString(action.route) || null,
    collection: toCleanString(action.collection) || null,
    label: toCleanString(action.label) || null,
    entityType: toCleanString(action.entity_type) || null,
    entityId:
      action.entity_id === undefined || action.entity_id === null
        ? null
        : action.entity_id,
    entity:
      action.entity && typeof action.entity === "object" ? action.entity : null,
  };
};

const normalizeUiActions = (responseData, structuredResponse) =>
  uniqueBy(
    toArray(responseData?.ui_actions || structuredResponse.uiActions)
      .map(normalizeUiAction)
      .filter(Boolean),
    (action) =>
      `${action.type}:${action.route || ""}:${action.collection || ""}:${
        action.entityType || ""
      }:${action.entityId || ""}`,
  );

const normalizeMessagesField = (messages) => {
  if (typeof messages === "string") {
    return humanizeInlineText(messages);
  }

  if (!Array.isArray(messages)) {
    return "";
  }

  return messages
    .map((item) => {
      if (typeof item === "string") {
        return item.trim();
      }

      if (item && typeof item === "object") {
        return humanizeInlineText(item.text || item.message || item.content);
      }

      return "";
    })
    .filter(Boolean)
    .join("\n");
};

const buildMetricSummary = (metrics = []) =>
  metrics
    .slice(0, 4)
    .map((metric) => `${humanizeLabel(metric.label)}: ${metric.value}`)
    .join(", ");

const extractResponseAnswer = (responseData) =>
  humanizeInlineText(responseData?.answer) ||
  humanizeInlineText(responseData?.message) ||
  normalizeMessagesField(responseData?.messages);

const getResolvedTables = (structuredResponse, contextTables) =>
  structuredResponse.tables.length ? structuredResponse.tables : contextTables;

const findRouteMatch = (value) => {
  const normalizedValue = toCleanString(value).toLowerCase();

  if (!normalizedValue) {
    return null;
  }

  return ROUTE_MATCHERS.find(({ keywords }) =>
    keywords.some((keyword) => normalizedValue.includes(keyword)),
  );
};

const buildNavigationActions = ({
  structuredResponse,
  contextTables,
  prompt,
  answer,
  uiActions = [],
}) => {
  const explicitActions = uiActions
    .filter((action) => action.type === "navigate" && action.route)
    .map((action) => ({
      id: action.id,
      label: action.label || "Open",
      route: action.route,
    }));

  const resolvedTables = getResolvedTables(structuredResponse, contextTables);
  const sources = [
    structuredResponse.responseType,
    structuredResponse.summary,
    prompt,
    answer,
    ...resolvedTables.map((table) => table.table),
  ];

  const actions = sources
    .map(findRouteMatch)
    .filter(Boolean)
    .map((match) => ({
      id: `route-${match.route}`,
      label: match.label,
      route: match.route,
    }));

  return uniqueBy([...explicitActions, ...actions], (item) => item.route).slice(
    0,
    3,
  );
};

const buildPromptSuggestions = ({
  structuredResponse,
  contextTables,
  prompt,
  answer,
}) => {
  if (structuredResponse.responseType) {
    const mappedSuggestions =
      RESPONSE_PROMPT_SUGGESTIONS[structuredResponse.responseType];

    if (mappedSuggestions?.length) {
      return normalizePromptSuggestions(mappedSuggestions);
    }
  }

  const resolvedTables = getResolvedTables(structuredResponse, contextTables);
  const sourceText = [
    prompt,
    answer,
    structuredResponse.summary,
    ...resolvedTables.map((table) => table.table),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (sourceText.includes("task")) {
    return normalizePromptSuggestions(RESPONSE_PROMPT_SUGGESTIONS.task_count);
  }

  if (sourceText.includes("matter") || sourceText.includes("case")) {
    return normalizePromptSuggestions(RESPONSE_PROMPT_SUGGESTIONS.matter_count);
  }

  return NORMALIZED_DEFAULT_PROMPT_SUGGESTIONS.slice(0, 3);
};

const buildBotText = (responseData, structuredResponse) => {
  const directAnswer = extractResponseAnswer(responseData);

  if (directAnswer) {
    return directAnswer;
  }

  if (structuredResponse.summary) {
    return structuredResponse.summary;
  }

  const metricSummary = buildMetricSummary(structuredResponse.metrics);

  if (metricSummary) {
    return metricSummary;
  }

  if (structuredResponse.tables.length) {
    return `I used ${structuredResponse.tables
      .map((table) => table.table)
      .join(", ")} to prepare this answer.`;
  }

  return DEFAULT_BOT_REPLY;
};

const buildAssistantMessage = (responseData, prompt) => {
  const structuredResponse = normalizeStructuredResponse(
    responseData?.structured_response,
    prompt,
  );
  const contextTables = normalizeContextTables(responseData?.context_tables);
  const uiActions = normalizeUiActions(responseData, structuredResponse);
  const answer = buildBotText(responseData, structuredResponse);
  const tableScopes = getResolvedTables(structuredResponse, contextTables);
  const suggestions = normalizePromptSuggestions([
    ...toArray(responseData?.suggestions),
    ...buildPromptSuggestions({
      structuredResponse,
      contextTables,
      prompt,
      answer,
    }),
  ]);

  return {
    id: createMessageId("bot"),
    text: answer,
    answer,
    sender: "bot",
    timestamp: new Date().toISOString(),
    structuredResponse,
    contextTables,
    summary: structuredResponse.summary || null,
    responseType: structuredResponse.responseType || null,
    metrics: structuredResponse.metrics,
    tableScopes,
    model: toCleanString(responseData?.model) || null,
    kind: toCleanString(responseData?.kind) || null,
    execution:
      responseData?.execution && typeof responseData.execution === "object"
        ? responseData.execution
        : null,
    uiActions,
    actions: buildNavigationActions({
      structuredResponse,
      contextTables,
      prompt,
      answer,
      uiActions,
    }),
    suggestions: suggestions.slice(0, 3),
  };
};

const buildNormalErrorText = (error) => {
  if (!error?.response) {
    return "Sorry, something went wrong.";
  }

  const { status, data } = error.response;

  if (status === 400) {
    if (Array.isArray(data?.missing_fields) && data.missing_fields.length) {
      return `Missing required fields: ${data.missing_fields.join(", ")}. ${data.detail || ""}`.trim();
    }

    if (data?.intent) {
      return `Intent not supported: ${data.intent}. ${data.detail || ""}`.trim();
    }

    return data?.detail || "Sorry, something went wrong.";
  }

  return data?.message || data?.detail || "Sorry, something went wrong.";
};

const buildNormalErrorMessage = (error) => ({
  id: createMessageId("error"),
  text: buildNormalErrorText(error),
  sender: "bot",
  timestamp: new Date().toISOString(),
  isError: true,
  suggestions: NORMALIZED_DEFAULT_PROMPT_SUGGESTIONS.slice(0, 3),
  actions: DEFAULT_CHAT_NAVIGATION.slice(0, 3),
});

const joinBackendUrl = (baseUrl, path) => {
  const normalizedBase = toCleanString(baseUrl).replace(/\/+$/, "");
  const normalizedPath = toCleanString(path).replace(/^\/+/, "");

  if (!normalizedBase) {
    return `/${normalizedPath}`;
  }

  if (!normalizedPath) {
    return normalizedBase;
  }

  return `${normalizedBase}/${normalizedPath}`;
};

export const useChat = () => {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }

  return context;
};

export const ChatProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const { token } = useSelector((state) => state.user);
  const conversationMetadataRef = useRef(null);

  const appendMessage = (message) => {
    setMessages((previousMessages) => [...previousMessages, message]);
  };

  const latestAssistantMessage = [...messages]
    .reverse()
    .find((message) => message.sender === "bot");

  const promptSuggestions =
    latestAssistantMessage?.suggestions?.length > 0
      ? latestAssistantMessage.suggestions
      : NORMALIZED_DEFAULT_PROMPT_SUGGESTIONS;

  const quickLinks =
    latestAssistantMessage?.actions?.length > 0
      ? latestAssistantMessage.actions
      : DEFAULT_CHAT_NAVIGATION;

  const applyUiActions = (uiActions) => {
    normalizeUiActions({ ui_actions: uiActions }, { uiActions: [] }).forEach(
      (action) => {
        if (action.type === "upsert_entity" && action.entityType === "task") {
          dispatch(upsertTaskFromLexmomAi(action.entity));
        }
      },
    );
  };

  const sendMessage = async (message) => {
    const trimmedMessage = toCleanString(message);

    if (!trimmedMessage || loading) {
      return { ok: false };
    }

    if (!token) {
      const errorText =
        "Your session has expired. Please sign in again to keep chatting.";
      appendMessage(
        createChatMessage("bot", errorText, {
          isError: true,
          suggestions: NORMALIZED_DEFAULT_PROMPT_SUGGESTIONS.slice(0, 3),
          actions: DEFAULT_CHAT_NAVIGATION.slice(0, 3),
        }),
      );
      return { ok: false };
    }

    appendMessage(createChatMessage("user", trimmedMessage));
    setLoading(true);

    try {
      const requestPayload = { prompt: trimmedMessage, user_context: null };
      if (conversationMetadataRef.current) {
        requestPayload.conversation_metadata = conversationMetadataRef.current;
      }

      const response = await axios.post(
        joinBackendUrl(backend_api, "lexmom-ai/chat/"),
        requestPayload,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      // Persist conversation_metadata for the next turn so the backend
      // can continue the active conversational flow (skip / confirm / etc.).
      const nextMetadata = response.data?.conversation_metadata;
      if (nextMetadata && Object.keys(nextMetadata).length > 0 && nextMetadata.active_intent) {
        conversationMetadataRef.current = nextMetadata;
      } else {
        // Flow ended or no active flow — clear so next message starts fresh.
        conversationMetadataRef.current = null;
      }

      applyUiActions(response.data?.ui_actions);
      const assistantMessage = buildAssistantMessage(response.data, trimmedMessage);
      appendMessage(assistantMessage);
      return { ok: true, data: response.data };
    } catch (error) {
      const errorMessage = buildNormalErrorMessage(error);
      appendMessage(errorMessage);
      return { ok: false, error };
    } finally {
      setLoading(false);
    }
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        loading,
        sendMessage,
        promptSuggestions,
        quickLinks,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
